// AppLoadingManager.js - Simplified and Corrected
class AppLoadingManager {
  constructor() {
    this.progress = 0;
    this.status = "Starting application...";
    this.subscribers = [];
    // Define initial loading steps and their relative weight
    this.steps = {
      "app-init": { weight: 5, completed: false }, // Basic app setup
      "auth-check": { weight: 10, completed: false }, // Checking auth status
      "settings-load": { weight: 10, completed: false }, // Loading user settings
      "core-components": { weight: 30, completed: false }, // Preloading critical JS bundles (like Calendar, Editor)
      "ui-render": { weight: 15, completed: false }, // Initial UI rendering setup
      "lazy-components-ready": { weight: 20, completed: false }, // Placeholder for lazy components being ready (can be triggered later)
      // Removed individual 'loaded-*' steps here for simplicity;
      // Track main phases instead. Add finer steps if granular tracking is truly needed.
    };

    // Calculate total weight for percentage calculations
    this.totalWeight = Object.values(this.steps).reduce(
      (acc, step) => acc + step.weight,
      0
    );

    // Ensure total weight is not zero to avoid division errors
    if (this.totalWeight === 0) {
      // console.warn(
      //   "AppLoadingManager: Total weight is zero, progress calculation might be incorrect."
      // );
      this.totalWeight = 1; // Avoid division by zero
    }

    // console.log("AppLoadingManager: Initialized with steps:", this.steps);
    // console.log("AppLoadingManager: Total weight:", this.totalWeight);
  }

  // Subscribe to progress updates
  subscribe(callback) {
    // console.log("AppLoadingManager: New subscriber added");
    this.subscribers.push(callback);
    // Notify immediately with current state
    callback({ progress: this.progress, status: this.status, step: null });
    // Return unsubscribe function
    return () => {
      // console.log("AppLoadingManager: Subscriber removed");
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  // Set progress manually - Use with caution, prefer completeStep
  setProgress(progress, status = null) {
    const newProgress = Math.min(100, Math.max(0, Math.round(progress)));
    // console.log(
    //   `AppLoadingManager: Setting progress directly to ${newProgress}%${
    //     status ? ` (${status})` : ""
    //   }`
    // );

    if (newProgress > this.progress) {
      this.progress = newProgress;
    } else if (progress === 0) {
      // Allow resetting to 0
      this.progress = 0;
    }

    if (status) {
      this.status = status;
    } else if (this.progress === 100) {
      this.status = "Application ready!"; // Default 100% message
    }

    this._notifySubscribers(null);
  }

  // Set status message explicitly
  setStatus(status) {
    // console.log(`AppLoadingManager: Setting status to "${status}"`);
    this.status = status;
    this._notifySubscribers(null);
  }

  // Mark a loading step as complete
  completeStep(stepName) {
    if (this.steps[stepName] && !this.steps[stepName].completed) {
      // console.log(`AppLoadingManager: Completed step "${stepName}"`);
      this.steps[stepName].completed = true;

      // Calculate current progress based on completed step weights
      const completedWeight = Object.values(this.steps)
        .filter((step) => step.completed)
        .reduce((acc, step) => acc + step.weight, 0);

      // Calculate progress percentage - ALLOWS 100%
      const newProgress = Math.min(
        100, // Allow reaching 100%
        Math.round((completedWeight / this.totalWeight) * 100)
      );

      // Update progress if it increased
      if (newProgress > this.progress) {
        this.progress = newProgress;
        // console.log(`AppLoadingManager: Progress updated to ${this.progress}%`);
      }

      // Update status only if progress reaches 100% via steps,
      // otherwise let setStatus control messages.
      if (this.progress === 100 && this.status !== "Application ready!") {
        this.setStatus("Finishing up..."); // Or keep the last set status
      }

      this._notifySubscribers(stepName);
    } else if (this.steps[stepName] && this.steps[stepName].completed) {
      // console.log(
      //   `AppLoadingManager: Step "${stepName}" was already completed.`
      // );
    } else if (!this.steps[stepName]) {
      // Step doesn't exist - log warning, do not add dynamically here.
      // console.warn(
      //   `AppLoadingManager: Attempted to complete non-existent step "${stepName}". Define steps during initialization or use addStep().`
      // );
    }
  }

  // Add a new step to track dynamically (if necessary)
  addStep(stepName, weight = 5) {
    if (!this.steps[stepName]) {
      // console.log(
      //   `AppLoadingManager: Added step "${stepName}" with weight ${weight}`
      // );
      this.steps[stepName] = { weight, completed: false };
      // Recalculate total weight
      this.totalWeight = Object.values(this.steps).reduce(
        (acc, step) => acc + step.weight,
        0
      );
      // console.log("AppLoadingManager: Total weight updated:", this.totalWeight);
    } else {
      // console.warn(`AppLoadingManager: Step "${stepName}" already exists.`);
    }
  }

  // Get the current loading state
  getState() {
    return {
      progress: this.progress,
      status: this.status,
      steps: { ...this.steps }, // Return a copy
    };
  }

  // Reset the loading state
  reset() {
    // console.log("AppLoadingManager: Resetting state");
    this.progress = 0;
    this.status = "Starting application...";
    Object.keys(this.steps).forEach((key) => {
      if (this.steps[key]) {
        // Check if step still exists before resetting
        this.steps[key].completed = false;
      }
    });
    // Recalculate total weight in case steps were added/removed before reset
    this.totalWeight =
      Object.values(this.steps).reduce((acc, step) => acc + step.weight, 0) ||
      1; // Avoid division by zero

    this._notifySubscribers(null);
  }

  // Private method to notify subscribers
  _notifySubscribers(completedStepName) {
    // Create payload once
    const payload = {
      progress: this.progress,
      status: this.status,
      step: completedStepName, // Pass the name of the step that was just completed
    };
    this.subscribers.forEach((callback) => {
      try {
        callback(payload);
      } catch (error) {
        console.error("AppLoadingManager: Error notifying subscriber:", error);
      }
    });
  }
}

// Singleton instance
const loadingManager = new AppLoadingManager();
export default loadingManager;
